"use client";

/**
 * `useFormKeyboard` — wires the platform's reserved form-scope keyboard
 * shortcuts (`Cmd+S`, `Cmd+Enter`, `Escape`) to caller-supplied
 * callbacks.
 *
 * The hook is the form-engine's bridge to `@aio/platform-keyboard`'s
 * `useHotkey`. Each shortcut is registered unconditionally — that is,
 * one `useHotkey` call per shortcut on every render — and the inner
 * handler short-circuits when the matching callback is `undefined`.
 * This keeps the call site free of conditional hook calls (which would
 * violate the rules-of-hooks) while still respecting the semantic
 * "if no callback is provided, the shortcut is a no-op".
 *
 * ## Why three separate hotkey registrations
 *
 * `useHotkey` accepts a single chord per call; `Cmd+S` / `Cmd+Enter` /
 * `Escape` are three independent chords that need three independent
 * keyboard listeners. We could collapse into one window-level handler,
 * but reusing `useHotkey` keeps the input-ignore/sequence/chord
 * semantics consistent with the rest of the platform (search, command
 * palette, list-row navigation) and avoids drifting handler logic.
 *
 * The shortcut identifiers themselves are reserved in
 * {@link RESERVED_SHORTCUTS} as `platform:save`, `platform:submit`, and
 * `platform:close` — the same constants the help overlay reads from —
 * so any user-facing list of shortcuts in the form scope reflects the
 * exact bindings this hook implements.
 *
 * ## Why callbacks are optional
 *
 * Forms in different shells can opt into different shortcut subsets.
 * A read-only detail page might wire `Escape` to dismiss but have
 * nothing to save; a wizard might wire `Cmd+Enter` to advance but
 * disable `Cmd+S` because the wizard saves implicitly. Making each
 * callback optional means the same hook covers all three cases
 * without requiring callers to pass a no-op function.
 *
 * Note: even when a callback is undefined, `useHotkey` still installs
 * its global listener and calls `e.preventDefault()` on a matching
 * keypress. That's intentional — it prevents the browser's default
 * `Cmd+S` (Save Page) and stray submits inside a form context, which
 * is the correct behaviour for the form scope. The "doesn't fire"
 * guarantee in the plan is about the user-supplied callback, not
 * about whether the chord is observed.
 *
 * ## Why a hook instead of a component
 *
 * Existing form composition wraps the form body in an `<form>` and
 * fires submit through rhf's `handleSubmit`. The shortcuts must
 * coexist with whatever JSX the form renders, including portals
 * (Dialogs, Popovers, Toasts) that live outside the form's DOM
 * subtree. A window-level listener (which is what `useHotkey` becomes)
 * is therefore the right shape — and a hook keeps that explicit at
 * the call site.
 *
 * Caller pattern:
 * ```tsx
 * function MyForm() {
 *   const form = useForm({ ... });
 *   useFormKeyboard({
 *     onSave: () => form.handleSubmit(persistDraft)(),
 *     onSubmit: () => form.handleSubmit(commit)(),
 *     onCancel: () => router.back(),
 *   });
 *   ...
 * }
 * ```
 */

import { useHotkey } from "@aio/platform-keyboard";

export interface UseFormKeyboardOptions {
  /**
   * Invoked on `Cmd+S` (or `Ctrl+S`). Typical wiring is a
   * draft-persist or autosave-flush — i.e. "stash what's here without
   * fully committing". Pair with `useAutosave` for the localStorage
   * draft path or with a dedicated "save draft" submit path on the
   * server. Optional; if omitted the shortcut is silently swallowed
   * (the browser's native Save Page is still suppressed).
   */
  onSave?: (() => void) | undefined;
  /**
   * Invoked on `Cmd+Enter` (or `Ctrl+Enter`). Typical wiring is the
   * primary submit path — `form.handleSubmit(commit)()`. Optional.
   */
  onSubmit?: (() => void) | undefined;
  /**
   * Invoked on `Escape`. Typical wiring is "discard and leave" —
   * `router.back()` or a custom cancel flow. Optional.
   */
  onCancel?: (() => void) | undefined;
}

/**
 * Registers the three reserved form-scope hotkeys against the supplied
 * callbacks. Always installs three listeners — callers should not
 * conditionally call this hook based on which callbacks they have. If
 * a callback is `undefined`, the corresponding shortcut becomes a
 * no-op (the chord is still consumed but no user code runs).
 */
export function useFormKeyboard(options: UseFormKeyboardOptions): void {
  const { onSave, onSubmit, onCancel } = options;

  // Each useHotkey invocation is unconditional and uses an arrow that
  // short-circuits on undefined. This keeps the hook call count stable
  // across renders (rules-of-hooks) while still respecting "if not
  // provided, doesn't fire" — the underlying useHotkey holds the
  // arrow in a ref so each render's latest closure (and therefore the
  // freshest `onSave`/`onSubmit`/`onCancel`) is what runs on a
  // matching keypress.
  useHotkey("Cmd+S", () => onSave?.());
  useHotkey("Cmd+Enter", () => onSubmit?.());
  useHotkey("Escape", () => onCancel?.());
}
